import { css } from '@emotion/react'
import styled from '@emotion/styled'
import {
  Gray100,
  Gray300,
  Gray400,
  Gray600,
  Gray900,
  Primary500,
} from '@mehilainen/mds-customer/colors'
import { Check } from '@mehilainen/mds-customer/icons'
import { Radio, RadioGroup, FormControl } from '@mui/material'
import equal from 'fast-deep-equal'
import React, { ReactNode, useState, useRef, useId } from 'react'

import { scale } from '../../utils/scale'
import { ColumnFlex } from '../Layout/Layout'
import { Text } from '../Typography/Typography'

const StyledFormControl = styled(FormControl)`
  width: 100%;
`

const StampContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`
const DescContainer = styled.div`
  grid-area: description;
`

const OptionContainer = styled(ColumnFlex)<{
  checked: boolean
  disabled: boolean
  focused: boolean
}>`
  position: relative;
  color: ${(props) => (props.disabled ? `${Gray600}` : `${Gray900}`)};
  background: ${(props) => (props.disabled ? `${Gray100}` : 'white')};
  padding: ${scale(2)};
  outline: ${(props) => (props.checked ? `2px solid ${Primary500}` : `1px solid ${Gray300}`)};
  border-radius: ${scale(0.5)};
  margin-bottom: ${scale(0.75)};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};

  ${(props) =>
    !props.disabled &&
    css`
      &:hover {
        outline: 2px solid ${Primary500};

        ${RadioIconWrapper} {
          border: 2px solid ${Primary500};
        }
      }
    `}

  ${(props) =>
    !props.disabled &&
    props.focused &&
    css`
      outline: 2px solid ${Primary500};

      ${RadioIconWrapper} {
        border: 2px solid ${Primary500};
        outline: 2px solid ${Primary500};
        outline-offset: 1px;
      }
    `}
`

const RadioIconWrapper = styled.div<{ focused: boolean; checked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: ${(props) => (props.checked ? 'none' : `1px solid ${Gray400}`)};
  background-color: ${(props) => (props.checked ? Primary500 : 'transparent')};

  ${(props) =>
    props.checked &&
    css`
      border: none;
      background-color: ${Primary500};
      .MuiSvgIcon-root {
        color: white;
      }
    `}
`

const Option = styled.div<{
  disabled: boolean
  hasDescription: boolean
}>`
  display: grid;
  grid-template-columns: 1fr 25fr;
  grid-template-rows: minmax(30px, 1fr);
  align-items: center;

  grid-template-areas: ${(props) =>
    props.hasDescription
      ? `'checkbox auto'
    '. description'`
      : `'checkbox auto'`};
`

const StyledRadio = styled(Radio)`
  &.MuiRadio-root {
    padding: 0;
    grid-area: checkbox;
    margin-right: 12px;
  }
`

const HeaderText = styled(Text)`
  grid-area: auto;
`

const RadioIcon: React.FC<{ checked: boolean; focused: boolean }> = ({ checked, focused }) => {
  return (
    <RadioIconWrapper checked={checked} focused={focused}>
      {checked && <Check />}
    </RadioIconWrapper>
  )
}

interface WrapperOptionProps<T> {
  option: T
  checked: boolean
  disabled: boolean
  dataCy?: string
  onChange(value: T): void
  renderOptionHeader(option: T, checked: boolean): ReactNode
  renderOptionDescription?(option: T, checked: boolean): ReactNode
  renderStamp?(option: T): ReactNode
}

const WrappedOption = <T,>({
  option,
  checked,
  disabled,
  dataCy,
  renderOptionHeader,
  renderOptionDescription,
  renderStamp,
  onChange,
}: WrapperOptionProps<T>) => {
  const [focused, setFocused] = useState<boolean>(false)
  const radioRef = useRef<HTMLInputElement>(null)
  const shouldRenderDescription = typeof renderOptionDescription === 'function'
  const shouldRenderStamp = typeof renderStamp === 'function' && renderStamp(option) !== undefined

  const id = useId()
  const optionId = `option-${id}`
  const optionHeaderId = `option-header-${id}`
  const optionDescriptionId = `option-description-${id}`

  const handleFocus = () => setFocused(true)
  const handleBlur = () => setFocused(false)

  const handleContainerClick = () => {
    if (!disabled) {
      if (radioRef.current) {
        radioRef.current.focus()
        radioRef.current.click()
      } else {
        onChange(option)
      }
    }
  }

  return (
    <OptionContainer
      focused={focused}
      checked={checked}
      disabled={disabled}
      data-cy={dataCy}
      onClick={handleContainerClick}
    >
      {shouldRenderStamp && <StampContainer>{renderStamp(option)}</StampContainer>}
      <Option disabled={disabled} hasDescription={shouldRenderDescription}>
        <StyledRadio
          checked={checked}
          disabled={disabled}
          onChange={() => onChange(option)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          id={optionId}
          inputProps={{
            'aria-labelledby': optionHeaderId,
            'aria-describedby': optionDescriptionId,
          }}
          inputRef={radioRef}
          icon={<RadioIcon checked={false} focused={focused} />}
          checkedIcon={<RadioIcon checked={true} focused={focused} />}
          disableRipple
        />
        <HeaderText as="span" id={optionHeaderId}>
          {renderOptionHeader(option, checked)}
        </HeaderText>
        {shouldRenderDescription && (
          <DescContainer id={optionDescriptionId}>
            {renderOptionDescription(option, checked)}
          </DescContainer>
        )}
      </Option>
    </OptionContainer>
  )
}

interface Props<T> {
  options: T[]
  value?: T | null
  onChange(value: T): void
  renderOptionHeader(option: T, checked: boolean): ReactNode
  renderOptionDescription?(option: T, checked: boolean): ReactNode
  renderStamp?(option: T): ReactNode
  isDisabled?(option: T): boolean
  isSelected?(selected: T, value?: T | null): boolean
  getKey?(option: T): string
  className?: string
  dataCy?: string
  radioLabelId?: string
}

const RadioButtonGroup = <T,>({
  options,
  value,
  renderOptionHeader,
  renderOptionDescription,
  renderStamp,
  onChange,
  isSelected = equal,
  isDisabled = () => false,
  getKey,
  className,
  dataCy,
  radioLabelId,
}: Props<T>) => {
  return (
    <StyledFormControl className={className} data-cy={dataCy} role="radiogroup">
      <RadioGroup aria-labelledby={radioLabelId}>
        {options.map((option, index) => {
          const checked = isSelected(option, value)
          const disabled = isDisabled(option)
          return (
            <WrappedOption
              key={getKey ? getKey(option) : `radioButtonGroup-option-${index}`}
              dataCy={getKey ? getKey(option) : `radioButtonGroup-option-${index}`}
              checked={checked}
              disabled={disabled}
              option={option}
              onChange={onChange}
              renderOptionHeader={renderOptionHeader}
              renderOptionDescription={renderOptionDescription}
              renderStamp={renderStamp}
            />
          )
        })}
      </RadioGroup>
    </StyledFormControl>
  )
}

export default RadioButtonGroup
